import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Range, getTrackBackground } from 'react-range';
import { ChartMountOptions, colorFor, getChartTheme, getDescriptionForVariant, getPaletteForVariant, getTitleForVariant } from './chartTheme';
import { exportChartAsImage } from './chartExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type EndpointResponse = {
  labels: Array<number | string>;
  datasets: Array<{ label: string; data: number[] }>;
};

export type InfographicsStackedBarProps = Omit<ChartMountOptions, 'chartType'>;

export function InfographicsStackedBar({ endpoint, height = 320, locale = 'de-DE' }: InfographicsStackedBarProps) {
  const [data, setData] = useState<EndpointResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [dataset,setDataset] = useState<string>("");
  const chartRef = useRef<ChartJS | null>(null);
  const [range, setRange] = useState<{ minIndex: number; maxIndex: number } | null>(null);

  const theme = useMemo(() => getChartTheme(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(endpoint+dataset, { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as EndpointResponse;
      })
      .then((json) => {
        if (cancelled) return;
        setData(json);
        if (json.labels && json.labels.length > 0) {
          setRange({ minIndex: 0, maxIndex: json.labels.length - 1 });
        } else {
          setRange(null);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint,dataset]);

  const chartData = useMemo(() => {
    if (!data) return null;

    const total = data.labels.length;
    if (total === 0) return null;

    const startIndex = range ? range.minIndex : 0;
    const endIndex = range ? range.maxIndex : total - 1;

    const from = Math.max(0, Math.min(startIndex, total - 1));
    const to = Math.max(from, Math.min(endIndex, total - 1));

    const palette = getPaletteForVariant(dataset);
    return {
      labels: data.labels.slice(from, to + 1).map(String),
      datasets: data.datasets.map((ds, index) => {
        const fallbackColor = colorFor(ds.label);
        const color = palette[index % palette.length] || fallbackColor;
        return {
          label: ds.label,
          data: ds.data.slice(from, to + 1),
          backgroundColor: color,
          borderColor: '#ffffff',
          borderSkipped: false,
          borderWidth: 1
        };
      })
    };
  }, [data, theme, range, dataset]);

  const options = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const cf = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    });
    const mrdFormat = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const mrdFormatAxis = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest' as const, intersect: true },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            color: theme.textColor,
            font: {
              family: theme.fontFamily
            }
          }
        },
        title: { display: false },
        tooltip: {
          titleFont: {
            family: theme.fontFamily
          },
          bodyFont: {
            family: theme.fontFamily
          },
          callbacks: {
            label: (ctx: any) => {
              const v = Number(ctx.raw ?? 0);
              return `${ctx.dataset.label}: ${mrdFormat.format(v / 1e9)} Mrd. €`;
            }
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            color: theme.gridColor
          },
          ticks: {
            color: theme.textColor,
            font: {
              family: theme.fontFamily
            }
          }
        },
        y: {
          stacked: true,
          ticks: {
            color: theme.textColor,
            font: {
              family: theme.fontFamily
            },
            callback: (value: any) => `${mrdFormatAxis.format(Number(value) / 1e9)} Mrd. €`
          },
          grid: {
            color: theme.gridColor
          }
        }
      }
    };
  }, [locale, theme]);

  if (loading) return <div>Diagramm wird geladen…</div>;
  if (error) return <div>Fehler beim Laden: {error}</div>;
  if (!chartData) return <div>Keine Daten.</div>;

  const title = getTitleForVariant(dataset);
  const description = getDescriptionForVariant(dataset);

  const FILENAMES: Record<string, string> = {
    '':               'klimafinanzierung-finanzierungstyp',
    '_financingtool': 'klimafinanzierung-finanzierungsinstrument',
    '_bereich':       'klimafinanzierung-bereich',
    '_incomegroups':  'klimafinanzierung-einkommensgruppen',
  };
  const handleDownload = () =>
    exportChartAsImage({ chart: chartRef.current, fileName: `${FILENAMES[dataset] ?? 'klimafinanzierung'}.png` });

  return (
    <div>
      {title && (
        <h2 className="ddk-font--base" style={{ margin: '0 0 0.75rem', padding: '0 1rem' }}>
          {title}
        </h2>
      )}
      <div
        style={{
          display: 'flex',
          gap: '2rem',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          padding: '0 1rem'
        }}
      >
        <div
          className="ddk-font--base"
          style={{
            minWidth: '220px',
            maxWidth: '260px',
            margin: '0 0.5rem'
          }}
        >
          <label className="ddk-font--base" style={{ display: 'block', marginBottom: '0.25rem', color: theme.controlColor }}>
            Aufschlüsseln nach:
          </label>
          <select
            className="ddk-font--base"
            style={{ color: theme.controlColor }}
            value={dataset}
            onChange={(e) => {
              setDataset(e.target.value);
            }}
          >
            <option value={""}>Finanzierungstyp</option>
            <option value={"_financingtool"}>Finanzierungsinstrument</option>
            <option value={"_incomegroups"}>Weltbank-Einkommensgruppen</option>
            <option value={"_bereich"}>Bereich</option>
          </select>
        </div>
        {data && range && data.labels.length > 0 && (
          <div
            className="ddk-font--base"
            style={{
              minWidth: '220px',
              maxWidth: '260px',
              margin: '0 0.5rem',
              color: theme.controlColor
            }}
          >
            <div>
              Zeitraum: {String(data.labels[range.minIndex])} – {String(data.labels[range.maxIndex])}
            </div>
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem 0',
              }}
            >
              <Range
                step={1}
                min={0}
                max={data.labels.length - 1}
                values={[range.minIndex, range.maxIndex]}
                onChange={(values: number[]) => {
                  const [minIndex, maxIndex] = values;
                  setRange({ minIndex, maxIndex });
                }}
                renderTrack={({ props, children }) => (
                  <div
                    onMouseDown={props.onMouseDown}
                    onTouchStart={props.onTouchStart}
                    style={{
                      ...props.style,
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      ref={props.ref}
                      style={{
                        height: '4px',
                        width: '100%',
                        borderRadius: '2px',
                        background: getTrackBackground({
                          values: [range.minIndex, range.maxIndex],
                          colors: [
                            theme.gridColor,
                            theme.controlColor,
                            theme.gridColor
                          ],
                          min: 0,
                          max: data.labels.length - 1
                        })
                      }}
                    >
                      {children}
                    </div>
                  </div>
                )}
                renderThumb={({ props, isDragged, index }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: '16px',
                      width: '16px',
                      borderRadius: '50%',
                      backgroundColor: '#ffffff',
                      border: `2px solid ${theme.controlColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 2px rgba(0,0,0,0.3)'
                    }}
                    aria-label={index === 0 ? 'Zeitraum Start' : 'Zeitraum Ende'}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '120%',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        color: theme.controlColor
                      }}
                    >
                      {String(data.labels[index === 0 ? range.minIndex : range.maxIndex])}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>
      <div style={{height: height}}>
        <Bar ref={chartRef as any} data={chartData as any} options={options as any} />
      </div>
      <div style={{ textAlign: 'right', padding: '0.25rem 1rem 0' }}>
        <button
          type="button"
          onClick={handleDownload}
          title="Als Bild herunterladen"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: theme.controlColor }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
      </div>
      {description && (
        <p className="ddk-font--base" style={{ margin: '0.75rem 0 0', padding: '0 1rem' }}>
          {description}
        </p>
      )}
    </div>
  );
}

